/**
 * "What kind of attachment is this File?" — decided in exactly one place.
 *
 * The composer accepts files three ways (click-pick, drag-and-drop,
 * clipboard paste) and all three funnel through here, so behaviour stays
 * consistent and the server never has to second-guess the UI.
 *
 * ## There is no type allowlist
 *
 * This module used to carry an `ALLOWED_MIME_PATTERNS` list and reject
 * anything outside it with `unsupported type: video/mp4`. That was the
 * banner in the recording: a 2.1 MiB `.mp4` was refused by the browser,
 * the draft text stayed behind, and the operator was told to retry or start
 * a new session. Type is not a reason to refuse an operator's file.
 *
 * The store keys its behaviour on type — an image gets a thumbnail, a video
 * gets a contact sheet, a CSV gets a schema — but *acceptance* depends on
 * exactly three things:
 *
 *   - **size**     — larger than {@link MAX_SESSION_FILE_BYTES}
 *   - **count**    — more than {@link MAX_SESSION_FILES_PER_MESSAGE} in one message
 *   - **unreadable** — zero bytes, or the browser cannot produce a handle
 *
 * Everything else — `video/mp4`, archives, `application/pdf`, `text/csv`,
 * and blobs with no recognizable type at all — is uploaded and stored.
 */

import {
  MAX_SESSION_FILE_BYTES,
  MAX_SESSION_FILES_PER_MESSAGE,
} from "./session-files.ts";

export type AttachmentRejectCode = "empty" | "too_large" | "too_many" | "unreadable";

export interface ClassifiedFile {
  /** The original File. */
  file: File;
  /** Best-effort MIME for display. Never used as an acceptance gate. */
  effectiveMime: string;
}

export interface RejectedFile {
  file: File;
  /** Human-readable reason, shown ON THE CHIP — never as a banner. */
  reason: string;
  code: AttachmentRejectCode;
}

export type ClassifyOutcome =
  | { ok: true; file: ClassifiedFile }
  | { ok: false; rejected: RejectedFile };

/**
 * Sniff a media type for display only.
 *
 * Browsers routinely send `""` for a dropped file and
 * `application/octet-stream` for anything they don't recognise, so we fall
 * back to a small extension table purely so the chip can say "MP4" instead
 * of showing nothing. An unknown extension yields "" — still accepted.
 */
const EXTENSION_MIME: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  svg: "image/svg+xml",
  heic: "image/heic",
  heif: "image/heif",
  tif: "image/tiff",
  tiff: "image/tiff",
  avif: "image/avif",
  mp4: "video/mp4",
  m4v: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  mkv: "video/x-matroska",
  avi: "video/x-msvideo",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  oga: "audio/ogg",
  opus: "audio/ogg",
  flac: "audio/flac",
  m4a: "audio/mp4",
  aac: "audio/aac",
  pdf: "application/pdf",
  zip: "application/zip",
  gz: "application/gz",
  tgz: "application/gzip",
  tar: "application/x-tar",
  "7z": "application/x-7z-compressed",
  rar: "application/vnd.rar",
  csv: "text/csv",
  tsv: "text/tab-separated-values",
  txt: "text/plain",
  log: "text/plain",
  md: "text/markdown",
  json: "application/json",
  ndjson: "application/x-ndjson",
  jsonl: "application/x-ndjson",
  xml: "text/xml",
  html: "text/html",
  htm: "text/html",
  css: "text/css",
  yaml: "text/yaml",
  yml: "text/yaml",
  toml: "text/toml",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  doc: "application/msword",
  xls: "application/vnd.ms-excel",
  ppt: "application/vnd.ms-powerpoint",
};

/** Display MIME: declared type wins, extension rescues the rest. */
export function effectiveMimeFor(file: Pick<File, "type" | "name">): string {
  const declared = (file.type ?? "").toLowerCase().trim();
  if (declared && declared !== "application/octet-stream") {
    return declared;
  }
  const ext = (file.name.split(".").pop() ?? "").toLowerCase();
  return EXTENSION_MIME[ext] ?? declared ?? "";
}

/**
 * Decide whether a file may be attached.
 *
 * `alreadyAttached` is how many files are already on the composer so the
 * count cap is enforced against the message being built rather than against
 * a session-wide limit.
 */
export function classifyFile(file: File, alreadyAttached = 0): ClassifyOutcome {
  if (!(file instanceof Object) || typeof file.size !== "number") {
    return {
      ok: false,
      rejected: { file, code: "unreadable", reason: "could not read this file" },
    };
  }
  if (file.size === 0) {
    return {
      ok: false,
      rejected: { file, code: "empty", reason: "file is empty" },
    };
  }
  if (file.size > MAX_SESSION_FILE_BYTES) {
    return {
      ok: false,
      rejected: {
        file,
        code: "too_large",
        reason: `file is too large (limit 25 MB)`,
      },
    };
  }
  if (alreadyAttached >= MAX_SESSION_FILES_PER_MESSAGE) {
    return {
      ok: false,
      rejected: {
        file,
        code: "too_many",
        reason: `a message can carry at most ${MAX_SESSION_FILES_PER_MESSAGE} files`,
      },
    };
  }
  return { ok: true, file: { file, effectiveMime: effectiveMimeFor(file) } };
}